import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import api from "../lib/api";
import { useAuth } from "./AuthContext";
import { xpForLevel, unlockedAccessories } from "../data/mochi";
import { applyVenueOverrides, getDestination } from "../data/destinations";
import { autoSeason } from "../data/season";

const AppStateContext = createContext(null);
export const useApp = () => useContext(AppStateContext);

const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));
const uid = () => Math.random().toString(36).slice(2, 9);
const nowISO = () => new Date().toISOString();

function isAnnivToday(anniv) {
  if (!anniv) return false;
  const a = new Date(anniv);
  const n = new Date();
  return a.getMonth() === n.getMonth() && a.getDate() === n.getDate();
}

function weekStartISO() {
  const d = new Date();
  const day = (d.getDay() + 6) % 7; // Monday start
  d.setDate(d.getDate() - day);
  d.setHours(0, 0, 0, 0);
  return d.toISOString();
}

// Kategori kencan yang sudah diselesaikan (persisten) — untuk kostum kupon kencan
export function completedCategories(s) {
  const set = new Set(s?.mochi?.dateCategories || []);
  (s?.bookings || []).forEach((b) => { if (b.status === "completed") { const c = getDestination(b.destId)?.category; if (c) set.add(c); } });
  return Array.from(set);
}

function ensureChallenges(s) {
  const ws = weekStartISO();
  if (!s.challenges || s.challenges.weekStart !== ws) {
    s.challenges = { weekStart: ws, careCount: 0, completedCount: 0, categories: [], claimed: [] };
  }
  return s.challenges;
}

function markSeason(s) {
  if (!s.seasonBadges) s.seasonBadges = [];
  const id = autoSeason();
  if (!s.seasonBadges.includes(id)) s.seasonBadges.push(id);
}

// gentle time-based decay so the pet feels alive but stays demo-friendly
function applyDecay(mochi) {
  if (!mochi.alive) return mochi;
  const last = new Date(mochi.lastUpdated || nowISO()).getTime();
  const hours = Math.min(48, (Date.now() - last) / 3600000);
  if (hours < 0.05) return mochi;
  const kenyang = clamp(mochi.kenyang - hours * 2.5);
  const energi = clamp(mochi.energi - hours * 1.8);
  const bahagia = clamp(mochi.bahagia - hours * 1.6);
  let kesehatan = mochi.kesehatan;
  if (kenyang < 12) kesehatan = clamp(kesehatan - hours * 1.5);
  return { ...mochi, kenyang, energi, bahagia, kesehatan, lastUpdated: nowISO() };
}

export function AppStateProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [anniversaryToday, setAnniversaryToday] = useState(false);
  const saveTimer = useRef(null);
  const stateRef = useRef(null);

  const grantAnnivIfDue = (s) => {
    if (!isAnnivToday(s.anniversary)) return false;
    const yr = new Date().getFullYear();
    if (s.anniversaryBonusYear === yr) return false;
    s.points += 200;
    s.anniversaryBonusYear = yr;
    s.mochi.bahagia = clamp(s.mochi.bahagia + 20);
    s.mochi.lastUpdated = nowISO();
    s.transactions.unshift({ id: uid(), label: "Kejutan hari jadian 💕", amount: 200, type: "credit", at: nowISO() });
    return true;
  };

  const persist = useCallback((next) => {
    stateRef.current = next;
    setState(next);
    try {
      localStorage.setItem(`kk_state_${user?.id || "anon"}`, JSON.stringify(next));
    } catch {}
    clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => {
      api.put("/state", { state: next }).catch(() => {});
    }, 450);
  }, [user]);

  // load from backend (fallback to localStorage cache)
  useEffect(() => {
    if (!user) { setState(null); setLoading(user === null); return; }
    let cancelled = false;
    (async () => {
      setLoading(true);
      let loaded = null;
      const cacheKey = `kk_state_${user.id}`;
      try {
        const cached = localStorage.getItem(cacheKey);
        if (cached) loaded = JSON.parse(cached);
      } catch {}
      try {
        try { const v = await api.get("/venues"); applyVenueOverrides(v.data); } catch (e) { /* venue admin opsional */ }
      const { data } = await api.get("/state");
        loaded = data;
      } catch {}
      if (cancelled || !loaded) { setLoading(false); return; }
      loaded.mochi = applyDecay(loaded.mochi);
      loaded.mochi.unlocked = unlockedAccessories(loaded.mochi.level);
      grantAnnivIfDue(loaded);
      ensureChallenges(loaded);
      if (!loaded.seasonBadges) loaded.seasonBadges = [];
      if (!loaded.holidaysClaimed) loaded.holidaysClaimed = [];
      if (!loaded.mochi.specialUnlocked) loaded.mochi.specialUnlocked = [];
      setAnniversaryToday(isAnnivToday(loaded.anniversary));
      stateRef.current = loaded;
      setState(loaded);
      setLoading(false);
      api.put("/state", { state: loaded }).catch(() => {});
    })();
    return () => { cancelled = true; };
  }, [user]);

  const update = useCallback((fn) => {
    const base = stateRef.current;
    if (!base) return;
    const next = fn(structuredClone(base));
    persist(next);
  }, [persist]);

  const addTx = (s, label, amount, type) => {
    s.transactions.unshift({ id: uid(), label, amount, type, at: nowISO() });
  };

  // ---- XP / level ----
  const grantXP = (s, amount) => {
    let m = s.mochi;
    m.xp += amount;
    while (m.xp >= xpForLevel(m.level)) {
      m.xp -= xpForLevel(m.level);
      m.level += 1;
      const bonus = m.level * 50;
      s.points += bonus;
      s.transactions.unshift({ id: uid(), label: `Bonus naik ke Level ${m.level} 🎉`, amount: bonus, type: "credit", at: nowISO() });
    }
    m.unlocked = unlockedAccessories(m.level);
  };

  // ---- Saved ----
  const toggleSaved = useCallback((id) => update((s) => {
    s.saved = s.saved.includes(id) ? s.saved.filter((x) => x !== id) : [...s.saved, id];
    return s;
  }), [update]);

  // ---- Bookings ----
  const createBooking = useCallback((payload) => {
    const id = "KK-" + uid().toUpperCase();
    update((s) => {
      const dest = getDestination(payload.destId);
      const base = dest.price * payload.guests;
      let discount = 0;
      let voucherId = null;
      if (payload.voucherId) {
        const v = s.vouchers.find((x) => x.id === payload.voucherId && !x.used);
        if (v && base >= v.minSpend) {
          discount = v.value;
          voucherId = v.id;
          v.used = true;
          v.reserved = id;
        }
      }
      const total = Math.max(0, base - discount);
      s.bookings.unshift({
        id, destId: payload.destId, destName: dest.name, city: dest.city,
        area: dest.area, img: dest.img, date: payload.date, time: payload.time,
        guests: payload.guests, base, discount, total, voucherId,
        status: "confirmed", note: payload.note || "", createdAt: nowISO(),
      });
      return s;
    });
    return id;
  }, [update]);

  const completeBooking = useCallback((id) => update((s) => {
    const b = s.bookings.find((x) => x.id === id);
    if (!b || b.status !== "confirmed") return s;
    b.status = "completed";
    b.completedAt = nowISO();
    const pts = Math.floor(b.total / 1000);
    b.pointsAwarded = pts;
    s.points += pts;
    addTx(s, `Kencan selesai · ${b.destName}`, pts, "credit");
    grantXP(s, 30 + Math.floor(pts / 5));
    s.mochi.bahagia = clamp(s.mochi.bahagia + 12);
    s.mochi.lastUpdated = nowISO();
    const ch = ensureChallenges(s);
    ch.completedCount += 1;
    const cat = getDestination(b.destId)?.category;
    if (cat && !ch.categories.includes(cat)) ch.categories.push(cat);
    if (cat) { s.mochi.dateCategories = Array.from(new Set([...(s.mochi.dateCategories || []), cat])); }
    return s;
  }), [update]);

  const cancelBooking = useCallback((id) => update((s) => {
    const b = s.bookings.find((x) => x.id === id);
    if (!b) return s;
    if (b.status === "completed" && b.pointsAwarded) {
      s.points = Math.max(0, s.points - b.pointsAwarded);
      addTx(s, `Batal · ${b.destName}`, b.pointsAwarded, "debit");
    }
    // return reserved voucher
    if (b.voucherId) {
      const v = s.vouchers.find((x) => x.id === b.voucherId);
      if (v && v.reserved === id) { v.used = false; v.reserved = null; }
    }
    b.status = "cancelled";
    return s;
  }), [update]);

  // ---- Rewards ----
  const redeemReward = useCallback((reward) => {
    let ok = false;
    update((s) => {
      if (s.points < reward.cost) return s;
      s.points -= reward.cost;
      s.vouchers.unshift({
        id: "V-" + uid().toUpperCase(), rewardId: reward.id, value: reward.value,
        minSpend: reward.minSpend, label: reward.label, used: false, createdAt: nowISO(),
      });
      addTx(s, `Tukar ${reward.label}`, reward.cost, "debit");
      ok = true;
      return s;
    });
    return ok;
  }, [update]);

  // ---- Mochi care ----
  const FEED_COST = 20;
  const MED_COST = 40;

  const feed = useCallback(() => update((s) => {
    if (s.points < FEED_COST || !s.mochi.alive) return s;
    s.points -= FEED_COST;
    s.mochi.kenyang = clamp(s.mochi.kenyang + 28);
    s.mochi.bahagia = clamp(s.mochi.bahagia + 6);
    addTx(s, "Beri makan Mochi", FEED_COST, "debit");
    grantXP(s, 10);
    ensureChallenges(s).careCount += 1; markSeason(s);
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const playWith = useCallback(() => update((s) => {
    if (!s.mochi.alive || s.mochi.energi < 10) return s;
    s.mochi.bahagia = clamp(s.mochi.bahagia + 18);
    s.mochi.energi = clamp(s.mochi.energi - 12);
    grantXP(s, 12);
    ensureChallenges(s).careCount += 1; markSeason(s);
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const cuddle = useCallback(() => update((s) => {
    if (!s.mochi.alive) return s;
    s.mochi.bahagia = clamp(s.mochi.bahagia + 12);
    grantXP(s, 6);
    ensureChallenges(s).careCount += 1; markSeason(s);
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const sleep = useCallback(() => update((s) => {
    if (!s.mochi.alive) return s;
    s.mochi.energi = clamp(s.mochi.energi + 40);
    s.mochi.bahagia = clamp(s.mochi.bahagia + 4);
    grantXP(s, 4);
    ensureChallenges(s).careCount += 1; markSeason(s);
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const medicate = useCallback(() => update((s) => {
    if (!s.mochi.alive || s.points < MED_COST) return s;
    s.points -= MED_COST;
    s.mochi.kesehatan = clamp(s.mochi.kesehatan + 45);
    addTx(s, "Obat untuk Mochi", MED_COST, "debit");
    grantXP(s, 8);
    ensureChallenges(s).careCount += 1; markSeason(s);
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const revive = useCallback(() => update((s) => {
    s.mochi.alive = true;
    s.mochi.kenyang = 60;
    s.mochi.bahagia = 65;
    s.mochi.energi = 60;
    s.mochi.kesehatan = 70;
    s.mochi.lastUpdated = nowISO();
    return s;
  }), [update]);

  const equipCostume = useCallback((id) => update((s) => {
    s.mochi.outfit = s.mochi.outfit === id ? null : id;
    return s;
  }), [update]);

  const renameMochi = useCallback((name) => update((s) => {
    const n = String(name || "").trim().slice(0, 16);
    if (n) s.mochi.name = n;
    return s;
  }), [update]);

  const equipAccessory = useCallback((accId) => update((s) => {
    const all = [...(s.mochi.unlocked || []), ...(s.mochi.specialUnlocked || [])];
    if (!all.includes(accId)) return s;
    s.mochi.equipped = s.mochi.equipped === accId ? null : accId;
    return s;
  }), [update]);

  const setActivePerson = useCallback((name) => update((s) => {
    s.activePerson = name;
    return s;
  }), [update]);

  const updateProfile = useCallback((fields) => {
    update((s) => {
      Object.assign(s, fields);
      if (fields.personA && s.activePerson !== fields.personB) s.activePerson = fields.personA;
      grantAnnivIfDue(s);
      return s;
    });
    if (isAnnivToday(fields.anniversary)) setAnniversaryToday(true);
  }, [update]);

  const addMemory = useCallback((bookingId, memory) => update((s) => {
    const b = s.bookings.find((x) => x.id === bookingId);
    if (b) b.memory = { ...(b.memory || {}), ...memory };
    return s;
  }), [update]);

  const claimChallenge = useCallback((chDef) => {
    let ok = false;
    update((s) => {
      const ch = ensureChallenges(s);
      if (ch.claimed.includes(chDef.id)) return s;
      const prog = chDef.metric === "categories" ? ch.categories.length
        : chDef.metric === "care" ? ch.careCount : ch.completedCount;
      if (prog < chDef.target) return s;
      ch.claimed.push(chDef.id);
      s.points += chDef.reward;
      addTx(s, `Tantangan: ${chDef.label}`, chDef.reward, "credit");
      ok = true;
      return s;
    });
    return ok;
  }, [update]);

  // ---- Holiday special missions ----
  const claimHoliday = useCallback((h) => {
    let ok = false;
    update((s) => {
      if (!s.holidaysClaimed) s.holidaysClaimed = [];
      if (s.holidaysClaimed.includes(h.id)) return s;
      const completed = (s.bookings || []).filter((b) => b.status === "completed").length;
      if (completed < h.mission.target) return s;
      s.holidaysClaimed.push(h.id);
      if (!s.mochi.specialUnlocked) s.mochi.specialUnlocked = [];
      if (!s.mochi.specialUnlocked.includes(h.reward.id)) s.mochi.specialUnlocked.push(h.reward.id);
      const bonus = h.bonus || 100;
      s.points += bonus;
      s.mochi.bahagia = clamp(s.mochi.bahagia + 15);
      s.mochi.lastUpdated = nowISO();
      addTx(s, `Misi ${h.label}: ${h.reward.label}`, bonus, "credit");
      ok = true;
      return s;
    });
    return ok;
  }, [update]);

  const value = {
    state, loading, FEED_COST, MED_COST, anniversaryToday,
    dismissAnniversary: () => setAnniversaryToday(false),
    toggleSaved, createBooking, completeBooking, cancelBooking,
    redeemReward, feed, playWith, cuddle, sleep, medicate, revive,
    equipAccessory, equipCostume, renameMochi, setActivePerson, updateProfile, addMemory, claimChallenge, claimHoliday,
  };

  return <AppStateContext.Provider value={value}>{children}</AppStateContext.Provider>;
}
